/**
 * ARAM Platform - Vertical Configuration (Frontend)
 * Core Desarrollos S.A.S - Copyright 2024-2026
 *
 * Maps backend module identifiers to sidebar navigation sections.
 * Used by Sidebar.tsx to dynamically show/hide nav items based on the
 * tenant's active vertical.
 */

export type VerticalKey = 'admin' | 'ultima_milla' | 'freight_forwarder' | 'multimodal' | 'distribution' | 'industria' | 'agroalimentario' | 'procesadoras' | 'mineria' | 'trazabilidad' | 'rental'

export interface VerticalInfo {
  name: string
  description: string
  icon: string
  color: string
  tagline: string
  target_clients: string[]
  key_features: string[]
  modules: string[]
}

/**
 * Mapping from sidebar route prefixes to backend module identifiers.
 * A nav item is visible if its module is included in `enabled_modules`.
 *
 * NOTE: "dashboard", "calendar" and "settings" are always visible (present
 * in all verticals) so they don't require explicit module tagging.
 */
export const ROUTE_MODULE_MAP: Record<string, string> = {
  // Freight Forwarding
  '/freight': 'freight',

  // Centro de Control
  '/tracking': 'tracking',

  // Gestión de Rutas (the section as a whole)
  '/routes': 'routes',

  // Sub-items inside routes that belong to specific modules
  '/weather': 'weather',
  '/emissions': 'emissions',

  // Flota y Activos
  '/fleet': 'fleet',

  // Tecnología IoT
  '/devices': 'iot',
  '/telemetry': 'iot',
  '/integrations': 'iot',
  '/lorawan': 'iot',

  // Inteligencia
  '/reports': 'reports',
  '/ai': 'ai',

  // World Monitor
  '/risk': 'risk',

  // Gestión de Activos Fijos
  '/fixed-assets': 'fixed_assets',

  // Organización
  '/hr': 'rrhh',
  '/projects': 'projects',

  // Finanzas
  '/accounting': 'accounting',
  '/fiscal': 'fiscal',
  '/treasury': 'treasury',

  // Tecnología
  '/traceability': 'traceability',
  '/rental': 'rental',
  '/drones': 'drones',
  '/documents/signing': 'digital_sign',

  // Agro
  '/fincas': 'agroalimentario',
  '/procesadora': 'procesadoras',

  // Última milla
  '/last-mile': 'ultima_milla',

  // Gestión comercial y distribución
  '/distribution': 'distribution',
  '/industrial': 'industrial',
}

/**
 * Routes always visible regardless of enabled_modules (settings, profile, etc.)
 */
const ALWAYS_VISIBLE_PREFIXES = ['/dashboard', '/settings', '/profile', '/notifications', '/communications', '/calendar', '/maps']

/**
 * Check if a given route should be visible for the provided enabled modules.
 *
 * - If enabled_modules contains '_full', all routes are visible (full-access vertical).
 * - System routes (dashboard, settings, notifications, etc.) are always visible.
 * - Routes in ROUTE_MODULE_MAP are visible only if their module is in enabled_modules.
 * - Unmapped routes are hidden for restricted users (no '_full').
 */
export function isRouteEnabled(route: string, enabledModules: string[]): boolean {
  // Full-access vertical: show everything
  if (enabledModules.includes('_full')) return true

  // Find the matching module for this route
  for (const [prefix, module] of Object.entries(ROUTE_MODULE_MAP)) {
    if (route.startsWith(prefix)) {
      return enabledModules.includes(module)
    }
  }

  // No mapping found — system routes are always visible
  if (ALWAYS_VISIBLE_PREFIXES.some(p => route === p || route.startsWith(p + '/'))) return true

  // If no modules at all (unexpected), allow
  if (enabledModules.length === 0) return true

  // Restricted user — hide unmapped routes
  return false
}

/**
 * Filter children of a nav section based on enabled modules.
 */
export function filterChildRoutes(
  children: Array<{ to: string; label: string; permission?: string }>,
  enabledModules: string[]
): Array<{ to: string; label: string; permission?: string }> {
  return children.filter((child) => isRouteEnabled(child.to, enabledModules))
}

/** All available verticals for admin selectors */
export const VERTICALS: { key: VerticalKey; label: string; shortLabel: string; icon: string; color: string; bgColor: string; description: string; group: 'admin' | 'logistica' | 'comercial' | 'industria' | 'trazabilidad' }[] = [
  // ── Modo administrador — sin restricciones ────────────────────────────────
  {
    key: 'admin',
    label: 'Admin (todo)',
    shortLabel: 'Admin',
    icon: 'shield',
    color: '#334155',
    bgColor: '#f8fafc',
    description: 'Vista completa sin restricciones — todos los módulos habilitados para revisión y soporte',
    group: 'admin',
  },
  // ── Modos logísticos ──────────────────────────────────────────────────────
  {
    key: 'ultima_milla',
    label: 'Última Milla',
    shortLabel: 'Últ. Milla',
    icon: 'package',
    color: '#3b82f6',
    bgColor: '#eff6ff',
    description: 'Reparto urbano optimizado, confirmación de entrega con firma digital',
    group: 'logistica',
  },
  {
    key: 'distribution',
    label: 'ARAM Distribution',
    shortLabel: 'Distribution',
    icon: 'store',
    color: '#0f766e',
    bgColor: '#f0fdfa',
    description: 'Clientes, ventas, stock, cobranzas y entregas en una operación integrada',
    group: 'comercial',
  },
  {
    key: 'industria',
    label: 'Industria Inteligente',
    shortLabel: 'Industria',
    icon: 'factory',
    color: '#2563eb',
    bgColor: '#eff6ff',
    description: 'Smart Warehouse, logística conectada y activos industriales en una operación autónoma',
    group: 'industria',
  },
  {
    key: 'freight_forwarder',
    label: 'Forwarder',
    shortLabel: 'Freight',
    icon: 'plane',
    color: '#8b5cf6',
    bgColor: '#f5f3ff',
    description: 'Expedientes digitales, coordinación multimodal y documentación de comercio exterior centralizada',
    group: 'logistica',
  },
  {
    key: 'multimodal',
    label: 'Logística & Distribución',
    shortLabel: 'Multimodal',
    icon: 'route',
    color: '#f59e0b',
    bgColor: '#fffbeb',
    description: 'Rutas optimizadas, gestión de flota, despacho y trazabilidad para operadores de carga terrestre',
    group: 'logistica',
  },
  // ── Verticales de industria ───────────────────────────────────────────────
  {
    key: 'agroalimentario',
    label: 'Agroindustria',
    shortLabel: 'Agro',
    icon: 'sprout',
    color: '#16a34a',
    bgColor: '#f0fdf4',
    description: 'Control de cosecha, insumos y cadena de frío — desde el campo hasta la planta',
    group: 'industria',
  },
  {
    key: 'procesadoras',
    label: 'Procesadoras & Manufactura',
    shortLabel: 'Procesadora',
    icon: 'factory',
    color: '#7c3aed',
    bgColor: '#f5f3ff',
    description: 'Trazabilidad de lotes, gestión de insumos y despacho integrado al sistema productivo',
    group: 'industria',
  },
  {
    key: 'mineria',
    label: 'Energía, Oil & Gas y Minería',
    shortLabel: 'Energía',
    icon: 'zap',
    color: '#ea580c',
    bgColor: '#fff7ed',
    description: 'Flota especializada, IoT en zonas remotas, compliance digital y trazabilidad de volúmenes',
    group: 'industria',
  },
  // ── Modo demo — solo Trazabilidad RFID ───────────────────────────────────
  {
    key: 'trazabilidad',
    label: 'Trazabilidad RFID',
    shortLabel: 'Trazabilidad',
    icon: 'scan',
    color: '#0891b2',
    bgColor: '#ecfeff',
    description: 'Modo demo enfocado exclusivamente en Trazabilidad RFID — oculta el resto de los módulos para presentaciones a clientes',
    group: 'trazabilidad',
  },
  {
    key: 'rental',
    label: 'Rental & Asset Traceability',
    shortLabel: 'Rental',
    icon: 'boxes',
    color: '#7c3aed',
    bgColor: '#f5f3ff',
    description: 'Alquiler de equipamiento con RFID, QR, devoluciones en tiempo real, limpieza y mantenimiento',
    group: 'trazabilidad',
  },
]

/** Regular users default to their tenant vertical; superadmins choose freely. */
export function getDefaultModeForTenant(vertical?: string): VerticalKey {
  const exactMode = VERTICALS.find(
    (candidate) => candidate.key !== 'admin' && candidate.key === vertical
  )
  return exactMode?.key ?? 'multimodal'
}

const CORE_VISIBLE_SECTIONS: Record<VerticalKey, string[] | null> = {
  admin: null,
  ultima_milla: ['/distribution'],
  freight_forwarder: ['/freight'],
  multimodal: ['/freight'],
  distribution: ['/distribution'],
  industria: ['/industrial'],
  agroalimentario: ['/fincas'],
  procesadoras: ['/procesadora'],
  mineria: ['/mining'],
  trazabilidad: ['/traceability'],
  rental: ['/rental'],
}

export function isCoreRouteVisibleForMode(route: string, mode: VerticalKey): boolean {
  const routes = CORE_VISIBLE_SECTIONS[mode]
  return routes === null || routes.includes(route)
}

// ─────────────────────────────────────────────────────────
// Mode-based sidebar section filtering
// ─────────────────────────────────────────────────────────

/**
 * Top-level sidebar sections visible per logistics mode.
 * Sections not listed here are ALWAYS visible regardless of mode:
 *   /dashboard, /calendar, /settings, /reports
 *
 * 'multimodal' shows everything (null = no filtering).
 */
export const MODE_VISIBLE_SECTIONS: Record<VerticalKey, string[] | null> = {
  // ── Modo administrador ────────────────────────────────────────────────────
  admin: null,   // null = show ALL sections, same as multimodal
  // ── Modos logísticos ──────────────────────────────────────────────────────
  ultima_milla: [
    '/dashboard',
    '/calendar',
    '/freight',          // Contenedor Logística; muestra el submódulo Última Milla
    '/tracking',         // Centro de Control
    '/routes',           // Planificación y Rutas
    '/fleet',            // Flota y Activos
    '/devices',          // Tecnología IoT
    '/reports',          // Business Intelligence
    '/settings',
  ],
  freight_forwarder: [
    '/dashboard',
    '/calendar',
    '/freight',          // Freight Orders
    '/tracking',         // Centro de Control (shipments, control tower)
    '/routes',           // Rutas, clima y emisiones habilitadas
    '/fleet',            // Flota y Activos
    '/devices',          // Tecnología IoT
    '/fiscal',           // Fiscal & Aduana
    '/reports',          // Business Intelligence
    '/risk',             // World Monitor
    '/settings',
  ],
  multimodal: null,    // null = show ALL sections
  distribution: [
    '/dashboard',
    '/calendar',
    '/distribution',
    '/freight',
    '/tracking',
    '/routes',
    '/fleet',
    '/accounting',
    '/fiscal',
    '/treasury',
    '/reports',
    '/documents',
    '/settings',
  ],
  industria: [
    '/dashboard',
    '/calendar',
    '/industrial',
    '/devices',
    '/fixed-assets',
    '/reports',
    '/documents',
    '/settings',
  ],
  // ── Verticales de industria ───────────────────────────────────────────────
  agroalimentario: [
    '/dashboard',
    '/calendar',
    '/fincas',           // Fincas & Campo (core)
    '/procesadora',      // Procesadora & Empaque (core)
    '/traceability',     // Trazabilidad RFID Agro (core)
    '/fleet',            // Flota y Activos (transporte)
    '/routes',           // Gestión de Rutas + Clima + Emisiones GLEC
    '/devices',          // Tecnología IoT (LoRaWAN, sensores)
    '/fiscal',           // Fiscal & Aduana (exportación)
    '/treasury',         // Tesorería
    '/drones',           // Drones (relevamiento)
    '/reports',          // Business Intelligence
    '/documents',        // Firma Digital
    '/settings',
  ],
  procesadoras: [
    '/dashboard',
    '/calendar',
    '/fincas',           // Contenedor Agro; muestra solo Procesadora & Empaque
    '/traceability',     // Trazabilidad RFID (lavandería industrial, manufactura)
    '/fleet',            // Flota y Activos (reparto/recogida)
    '/routes',           // Gestión de Rutas
    '/devices',          // Tecnología IoT (RFID readers)
    '/reports',          // Business Intelligence
    '/documents',        // Firma Digital
    '/settings',
  ],
  mineria: [
    '/dashboard',
    '/calendar',
    '/mining',           // Minería (dashboard + expediciones + servicios)
    '/fleet',            // Flota y Activos
    '/tracking',         // Centro de Control (zonas remotas)
    '/routes',           // Gestión de Rutas + Clima
    '/devices',          // Tecnología IoT (LoRaWAN, telemetría)
    '/risk',             // World Monitor (seguridad en sitio)
    '/treasury',         // Tesorería (contratos y servicios)
    '/drones',           // Drones (inspección de sitio)
    '/reports',          // Business Intelligence
    '/documents',        // Firma Digital
    '/settings',
  ],
  // ── Modo demo — solo Trazabilidad RFID ───────────────────────────────────
  trazabilidad: [
    '/dashboard',
    '/calendar',
    '/traceability',     // Trazabilidad RFID (único módulo visible)
    '/settings',
  ],
  rental: [
    '/dashboard',
    '/calendar',
    '/rental',
    '/traceability',
    '/devices',
    '/reports',
    '/settings',
  ],
}

/**
 * Check if a sidebar section should be visible for the given logistics mode.
 */
export function isSectionVisibleForMode(sectionTo: string, mode: VerticalKey): boolean {
  const allowed = MODE_VISIBLE_SECTIONS[mode as keyof typeof MODE_VISIBLE_SECTIONS]
  if (allowed === undefined) return true // clave desconocida (ej: localStorage viejo) → mostrar todo
  if (allowed === null) return true      // multimodal → everything
  return allowed.includes(sectionTo)
}

/**
 * Keep the shared Logistics section focused on the selected logistics mode.
 * The parent route is `/freight`, while Last Mile lives at `/last-mile`.
 */
export function isLogisticsChildVisibleForMode(route: string, mode: VerticalKey): boolean {
  if (mode === 'multimodal' || mode === 'admin') return true
  if (route.startsWith('/procesadora')) {
    return mode === 'agroalimentario' || mode === 'procesadoras'
  }
  if (route.startsWith('/fincas')) return mode === 'agroalimentario'
  if (route.startsWith('/last-mile')) return mode === 'ultima_milla' || mode === 'distribution'
  if (route.startsWith('/freight')) return mode === 'freight_forwarder'
  return true
}
